'use client';

import { useState, useMemo } from 'react';
import { DIAGNOSTIC_QUESTIONS, QuestionOption, Question } from '@/lib/diagnosticQuestions';
import { ENHANCED_DIAGNOSTIC_QUESTIONS, getConditionalQuestions } from '@/lib/enhancedDiagnosticQuestions';
import { scoreUserForPaths, getPathById } from '@/lib/careerPaths';
import { ScoringResult } from '@/lib/advancedScoring';
import styles from './DiagnosticWizard.module.css';
import VoiceInput from './VoiceInput';
import { enhanceCareerResult } from '@/lib/data/jobTitleAliases';
import { formatCareerTitle } from '@/lib/utils/titleFormatter';
import {
  getSegmentConfig,
  getQuestionOverride,
  enhanceMatchReasons,
  getResultsSummary,
} from '@/lib/segmentPersonalization';
import {
  PASSION_CATEGORIES,
  getAlignmentLabel,
  type PassionId,
} from '@/lib/passionMapping';
import {
  getViabilityLabel,
  getInvestmentLabel,
} from '@/lib/gigEconomy';
import {
  getAutomationRiskLabel,
  getAIRelationshipLabel,
  getAIImpactTimelineLabel,
} from '@/lib/aiResilience';
import ResumeGenerator from './ResumeGenerator';
import PremiumToolsNavigator from './PremiumToolsNavigator';
import SuccessStorySubmission from './SuccessStorySubmission';
import { usePremiumStatus } from '@/hooks/usePremiumStatus';

// Feature flag to enable enhanced questions (set to true to use military/aspiration questions)
const USE_ENHANCED_QUESTIONS = true;

interface DiagnosticWizardProps {
  initialSegment?: string | null;
  devMode?: boolean; // Bypass paywall for testing (use ?devtest=true or ?unlocked=true)
}

export default function DiagnosticWizard({ initialSegment, devMode = false }: DiagnosticWizardProps) {
  // Get segment-specific configuration for personalization
  const segmentConfig = useMemo(() => getSegmentConfig(initialSegment), [initialSegment]);
  const userSegment = initialSegment;
  const [step, setStep] = useState(0);

  // Check premium status from Supabase and localStorage
  const premiumStatus = usePremiumStatus();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [result, setResult] = useState<ScoringResult | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [pathways, setPathways] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [pathwayMetadata, setPathwayMetadata] = useState<any>(null);
  const [twoStepSubstep, setTwoStepSubstep] = useState<number>(0); // Track substep for two_step questions
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [showSuccessStory, setShowSuccessStory] = useState(false);

  // Handle premium checkout
  const handlePremiumCheckout = async () => {
    setIsCheckoutLoading(true);
    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: 'career_blueprint',
        }),
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('Checkout error:', data.error);
        alert('Unable to start checkout. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again.');
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  // Get the appropriate question set and filter conditionally
  const activeQuestions = useMemo(() => {
    if (!USE_ENHANCED_QUESTIONS) {
      return DIAGNOSTIC_QUESTIONS;
    }

    // Get the list of question IDs that should be shown based on answers
    const questionIds = getConditionalQuestions(answers);

    // Map IDs to actual question objects
    return questionIds.map(id =>
      ENHANCED_DIAGNOSTIC_QUESTIONS.find(q => q.id === id)
    ).filter((q): q is Question => q !== undefined);
  }, [answers]);

  const currentQuestion = activeQuestions[step];

  const handleAnswer = (value: string | string[]) => {
    // For two_step questions, store answers for each substep separately
    if (currentQuestion.type === 'two_step' && currentQuestion.substeps) {
      const substep = currentQuestion.substeps[twoStepSubstep];
      const newAnswers = {
        ...answers,
        [substep.id]: value
      };
      setAnswers(newAnswers);
    } else {
      const newAnswers = { ...answers, [currentQuestion.id]: value };
      setAnswers(newAnswers);
    }
  };

  const handleNext = async () => {
    // Handle two-step questions
    if (currentQuestion.type === 'two_step' && currentQuestion.substeps) {
      // Special handling for military question: skip branch selection if user answered "no"
      if (currentQuestion.id === 'q5_military_background' && twoStepSubstep === 0) {
        const servedAnswer = answers.q5a_served;
        if (servedAnswer === 'no') {
          // Skip the branch selection substep, move to next question
          setTwoStepSubstep(0);
          setStep(step + 1);
          return;
        }
      }
      
      if (twoStepSubstep < currentQuestion.substeps.length - 1) {
        // Move to next substep
        setTwoStepSubstep(twoStepSubstep + 1);
        return;
      } else {
        // Completed all substeps, move to next question
        setTwoStepSubstep(0);
      }
    }

    if (step < activeQuestions.length - 1) {
      setStep(step + 1);
    } else {
      // Save diagnostic responses to Supabase (including user segment for analytics)
      try {
        const diagnosticResponse = await fetch('/api/diagnostic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers, userSegment }),
        });

        const diagnosticData = await diagnosticResponse.json();
        
        if (!diagnosticData.success) {
          console.error('Failed to save diagnostic:', diagnosticData.error);
        }

        // Fetch career matches from API
        const matchResponse = await fetch('/api/careers/match', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            answers,
            sessionId: diagnosticData.sessionId 
          }),
        });

        const matchData = await matchResponse.json();

        if (matchData.success && matchData.matches.length > 0) {
          setResult(matchData.matches[0]);
          // Store pathway data if available
          if (matchData.pathways && matchData.pathways.length > 0) {
            setPathways(matchData.pathways);
            setPathwayMetadata(matchData.pathway_metadata);
          }
        } else {
          // Fallback to local scoring
          const scores = scoreUserForPaths(answers);
          const topMatch = scores[0];
          const path = getPathById(topMatch.pathId);
          if (path) {
            setResult({
              career: path,
              composite_score: topMatch.score,
              scores: { skill_match: topMatch.score, lifestyle_fit: 0, certification_match: 0, market_viability: 0, leadership_fit: 0, salary_fit: 0, training_feasibility: 0 },
              confidence: 'medium',
              match_reasons: []
            });
          }
        }
      } catch (error) {
        console.error('Error processing diagnostic:', error);
        // Fallback to local scoring
        const scores = scoreUserForPaths(answers);
        const topMatch = scores[0];
        const path = getPathById(topMatch.pathId);
        if (path) {
          setResult({
            career: path,
            composite_score: topMatch.score,
            scores: { skill_match: topMatch.score, lifestyle_fit: 0, certification_match: 0, market_viability: 0, leadership_fit: 0, salary_fit: 0, training_feasibility: 0 },
            confidence: 'medium',
            match_reasons: []
          });
        }
      }
    }
  };

  const handleBack = () => {
    // Handle two-step questions
    if (currentQuestion.type === 'two_step' && twoStepSubstep > 0) {
      // Go back to previous substep
      setTwoStepSubstep(twoStepSubstep - 1);
      return;
    }

    if (step > 0) {
      setStep(step - 1);
      // Reset substep when going back to a previous question
      const prevQuestion = activeQuestions[step - 1];
      if (prevQuestion.type === 'two_step' && prevQuestion.substeps) {
        setTwoStepSubstep(prevQuestion.substeps.length - 1);
      }
    }
  };

  // Get current answer based on question type
  let currentAnswer;
  if (currentQuestion.type === 'two_step' && currentQuestion.substeps) {
    const substep = currentQuestion.substeps[twoStepSubstep];
    currentAnswer = answers[substep.id];
  } else {
    currentAnswer = answers[currentQuestion.id];
  }

  const canProceed = currentAnswer !== undefined && currentAnswer !== '';

  if (result) {
    const { career, composite_score, scores, confidence, match_reasons, passion_score, passion_alignments, freelance_data, ai_resilience, enhanced_data } = result;

    // Check premium status: devMode OR Supabase/localStorage purchase
    const isPremium = devMode || premiumStatus.isPremium;
    // Check if user has Complete tier (includes resume builder)
    const hasCompleteTier = devMode || premiumStatus.hasCompleteTier;

    // Enhance career with actionable job titles
    const enhancedCareer = enhanceCareerResult({
      title: career.title,
      description: career.description,
      salary: career.salary
    });

    // Enhance match reasons with segment-specific context
    const enhancedMatchReasons = enhanceMatchReasons(
      userSegment,
      match_reasons,
      { keywords: career.keywords, category: career.category }
    );

    // Get segment-specific result summary
    const resultSummary = getResultsSummary(userSegment, formatCareerTitle(career.title), composite_score);

    // Prepare passion alignment info for display
    const hasPassionAlignment = passion_alignments && passion_alignments.length > 0;

    return (
      <div className={styles.resultContainer}>
        {/* Segment-specific headline */}
        <div className={styles.preheader}>{segmentConfig.resultsHeadline}</div>
        <h1 className={styles.title}>{formatCareerTitle(career.title)}</h1>

        {/* Personalized summary */}
        {userSegment && (
          <p className={styles.resultSummary}>{resultSummary}</p>
        )}
        
        <div className={styles.matchScore}>
          <div className={styles.scoreCircle}>
            <span className={styles.scoreNumber}>{composite_score}</span>
            <span className={styles.scoreLabel}>Match Score</span>
          </div>
          <div className={styles.confidenceBadge}>
            {confidence.toUpperCase()} CONFIDENCE
          </div>
        </div>

        {/* Phase 3: Passion Alignment Display */}
        {hasPassionAlignment && (
          <div className={styles.passionAlignment}>
            <div className={styles.passionHeader}>
              <span className={styles.passionIcon}>🎯</span>
              <span className={styles.passionLabel}>Aligns with your passions!</span>
            </div>
            <div className={styles.passionTags}>
              {passion_alignments?.map((alignment, i) => {
                const passionInfo = PASSION_CATEGORIES[alignment.passion_id as PassionId];
                return (
                  <span key={i} className={styles.passionTag}>
                    {passionInfo?.icon || '❤️'} {passionInfo?.label || alignment.passion_id}
                    <span className={styles.alignmentType}>
                      {getAlignmentLabel(alignment.alignment_type)}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Phase 4: Freelance/Gig Economy Display */}
        {freelance_data && freelance_data.viability_score >= 50 && (
          <div className={styles.freelanceSection}>
            <div className={styles.freelanceHeader}>
              <span className={styles.freelanceIcon}>💼</span>
              <span className={styles.freelanceLabel}>{getViabilityLabel(freelance_data.viability)}</span>
              <span className={styles.freelanceScore}>{freelance_data.viability_score}%</span>
            </div>

            {freelance_data.profile && (
              <div className={styles.freelanceDetails}>
                <div className={styles.freelanceStats}>
                  <div className={styles.freelanceStat}>
                    <span className={styles.statLabel}>Side Hustle Income:</span>
                    <span className={styles.statValue}>
                      ${freelance_data.profile.side_hustle_income.min.toLocaleString()} - ${freelance_data.profile.side_hustle_income.max.toLocaleString()}/mo
                    </span>
                  </div>
                  <div className={styles.freelanceStat}>
                    <span className={styles.statLabel}>Time to First Client:</span>
                    <span className={styles.statValue}>{freelance_data.profile.time_to_first_client}</span>
                  </div>
                  <div className={styles.freelanceStat}>
                    <span className={styles.statLabel}>Startup Investment:</span>
                    <span className={styles.statValue}>{getInvestmentLabel(freelance_data.profile.startup_investment)}</span>
                  </div>
                </div>

                {freelance_data.platforms.length > 0 && (
                  <div className={styles.gigPlatforms}>
                    <h4>Top Gig Platforms:</h4>
                    <div className={styles.platformsList}>
                      {freelance_data.platforms.slice(0, 4).map((platform, i) => (
                        <a
                          key={i}
                          href={platform.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.platformLink}
                        >
                          <span className={styles.platformName}>{platform.name}</span>
                          <span className={styles.platformRate}>
                            ${platform.avg_hourly_range.min}-${platform.avg_hourly_range.max}/hr
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {freelance_data.profile.typical_project_types.length > 0 && (
                  <div className={styles.projectTypes}>
                    <h4>Typical Freelance Projects:</h4>
                    <div className={styles.projectTags}>
                      {freelance_data.profile.typical_project_types.slice(0, 4).map((project, i) => (
                        <span key={i} className={styles.projectTag}>{project}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Phase 5: AI Resilience Display */}
        {ai_resilience && ai_resilience.profile && (
          <div className={styles.aiResilienceSection}>
            <div className={styles.aiResilienceHeader}>
              <span className={styles.aiResilienceIcon}>🛡️</span>
              <span className={styles.aiResilienceLabel}>
                {getAutomationRiskLabel(ai_resilience.profile.automation_risk_level)}
              </span>
              <span className={`${styles.aiResilienceScore} ${styles[ai_resilience.profile.automation_risk_level.replace('_', '')]}`}>
                {ai_resilience.resilience_score}% Safe
              </span>
            </div>

            <div className={styles.aiResilienceDetails}>
              <div className={styles.aiResilienceInfo}>
                <div className={styles.aiInfoItem}>
                  <span className={styles.aiInfoLabel}>AI Relationship:</span>
                  <span className={styles.aiInfoValue}>
                    {getAIRelationshipLabel(ai_resilience.profile.ai_relationship)}
                  </span>
                </div>
                <div className={styles.aiInfoItem}>
                  <span className={styles.aiInfoLabel}>AI Impact Timeline:</span>
                  <span className={styles.aiInfoValue}>
                    {getAIImpactTimelineLabel(ai_resilience.profile.ai_impact_timeline)}
                  </span>
                </div>
              </div>

              {ai_resilience.profile.resilience_factors.length > 0 && (
                <div className={styles.resilienceFactors}>
                  <h4>Why This Career is Future-Proof:</h4>
                  <ul>
                    {ai_resilience.profile.resilience_factors.slice(0, 4).map((factor, i) => (
                      <li key={i}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}

              {ai_resilience.tools.length > 0 && (
                <div className={styles.aiTools}>
                  <h4>AI Tools to Boost Your Productivity:</h4>
                  <div className={styles.toolsList}>
                    {ai_resilience.tools.slice(0, 4).map((tool, i) => (
                      <div key={i} className={styles.toolItem}>
                        <span className={styles.toolName}>{tool.name}</span>
                        <span className={styles.toolDifficulty}>
                          {tool.learning_difficulty === 'easy' ? '⚡ Easy to learn' :
                           tool.learning_difficulty === 'medium' ? '📚 Moderate learning curve' : '🎓 Advanced skill'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ai_resilience.skills.length > 0 && (
                <div className={styles.futureProofSkills}>
                  <h4>Skills to Develop for Long-Term Security:</h4>
                  <div className={styles.skillCards}>
                    {ai_resilience.skills.slice(0, 3).map((skill, i) => (
                      <div key={i} className={styles.futureSkillCard}>
                        <span className={styles.futureSkillName}>{skill.name}</span>
                        <span className={styles.futureSkillReason}>{skill.ai_cant_replace_because}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ai_resilience.profile.evolution_path && (
                <div className={styles.evolutionPath}>
                  <h4>Career Evolution Tip:</h4>
                  <p>{ai_resilience.profile.evolution_path}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Phase 6: Success Stories - Premium Gated */}
        {enhanced_data && enhanced_data.success_stories.length > 0 && (
          <div className={styles.successStoriesSection}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>⭐</span>
              <h3>Success Stories</h3>
              {!isPremium && <span className={styles.premiumBadge}>🔒 Blueprint</span>}
            </div>
            {isPremium ? (
              <div className={styles.storiesGrid}>
                {enhanced_data.success_stories.map((story, i) => (
                  <div key={i} className={styles.storyCard}>
                    <div className={styles.storyHeader}>
                      <div className={styles.storyAvatar}>{story.avatar_initials}</div>
                      <div className={styles.storyMeta}>
                        <span className={styles.storyName}>{story.name}</span>
                        <span className={styles.storyTransition}>
                          {story.previous_role} → {story.current_role}
                        </span>
                      </div>
                      {story.verified && (
                        <span className={styles.verifiedBadge}>✓ Verified</span>
                      )}
                    </div>
                    <blockquote className={styles.storyQuote}>
                      &ldquo;{story.quote}&rdquo;
                    </blockquote>
                    <div className={styles.storyDetails}>
                      <span className={styles.storyTime}>⏱️ {story.transition_time}</span>
                      {story.salary_increase && (
                        <span className={styles.storySalary}>💰 {story.salary_increase}</span>
                      )}
                      {story.location && (
                        <span className={styles.storyLocation}>📍 {story.location}</span>
                      )}
                    </div>
                    {story.key_steps.length > 0 && (
                      <div className={styles.storySteps}>
                        <strong>Key steps:</strong>
                        <ol>
                          {story.key_steps.slice(0, 3).map((step, j) => (
                            <li key={j}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.lockedContent}>
                <div className={styles.lockedPreview}>
                  <div className={styles.blurredCard}>
                    <div className={styles.storyHeader} style={{ opacity: 0.4 }}>
                      <div className={styles.storyAvatar}>{enhanced_data.success_stories[0]?.avatar_initials || 'JD'}</div>
                      <div className={styles.storyMeta}>
                        <span className={styles.storyName}>████████</span>
                        <span className={styles.storyTransition}>████ → {formatCareerTitle(career.title)}</span>
                      </div>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                      &ldquo;████████████████████████████████...&rdquo;
                    </p>
                  </div>
                </div>
                <div className={styles.lockedOverlay}>
                  <span className={styles.lockIcon}>🔒</span>
                  <p><strong>{enhanced_data.success_stories.length} success stories</strong> from people who became {formatCareerTitle(career.title)}s</p>
                  <p className={styles.lockedHint}>Learn their exact steps, timeline, and salary increases</p>
                  <button
                    className={styles.unlockButton}
                    onClick={handlePremiumCheckout}
                    disabled={isCheckoutLoading}
                  >
                    {isCheckoutLoading ? 'Loading...' : 'Unlock Blueprint - $29'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 6: "People Like You" Journey Profiles - Premium Gated */}
        {enhanced_data && enhanced_data.journey_profiles.length > 0 && (
          <div className={styles.journeySection}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>🗺️</span>
              <h3>People Like You</h3>
              {!isPremium && <span className={styles.premiumBadge}>🔒 Blueprint</span>}
            </div>
            {isPremium ? (
              enhanced_data.journey_profiles.map((journey, i) => (
                <div key={i} className={styles.journeyCard}>
                  <div className={styles.journeyHeader}>
                    <h4>{journey.persona_name}</h4>
                    <span className={styles.journeyAge}>{journey.age_range}</span>
                  </div>
                  <p className={styles.journeyDescription}>{journey.description}</p>
                  <div className={styles.journeyPath}>
                    {journey.career_path.map((step, j) => (
                      <div key={j} className={styles.journeyStep}>
                        <span className={styles.stepNumber}>{step.step}</span>
                        <div className={styles.stepContent}>
                          <span className={styles.stepRole}>{step.role}</span>
                          <span className={styles.stepDuration}>{step.duration}</span>
                          <span className={styles.stepAction}>{step.key_action}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.journeyOutcome}>
                    <span className={styles.outcomeLabel}>Outcome:</span>
                    <span className={styles.outcomeRole}>{journey.outcome.final_role}</span>
                    <span className={styles.outcomeSalary}>
                      ${(journey.outcome.salary_range.min / 1000).toFixed(0)}K - ${(journey.outcome.salary_range.max / 1000).toFixed(0)}K
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.lockedContent}>
                <div className={styles.lockedPreview}>
                  <div className={styles.blurredCard}>
                    <div style={{ opacity: 0.4 }}>
                      <h4 style={{ color: 'rgba(255,255,255,0.5)' }}>The Self-Taught ████████</h4>
                      <div className={styles.journeyPath}>
                        <div className={styles.journeyStep}>
                          <span className={styles.stepNumber}>1</span>
                          <div className={styles.stepContent}>
                            <span className={styles.stepRole}>████████</span>
                            <span className={styles.stepDuration}>3-6 months</span>
                          </div>
                        </div>
                        <div className={styles.journeyStep}>
                          <span className={styles.stepNumber}>2</span>
                          <div className={styles.stepContent}>
                            <span className={styles.stepRole}>████████</span>
                            <span className={styles.stepDuration}>████</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.lockedOverlay}>
                  <span className={styles.lockIcon}>🔒</span>
                  <p><strong>Step-by-step journey</strong> from someone just like you</p>
                  <p className={styles.lockedHint}>See exactly how they went from {userSegment || 'where you are'} to {formatCareerTitle(career.title)}</p>
                  <button
                    className={styles.unlockButton}
                    onClick={handlePremiumCheckout}
                    disabled={isCheckoutLoading}
                  >
                    {isCheckoutLoading ? 'Loading...' : 'Unlock Blueprint - $29'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 6: Top Locations with Salary Data - Premium Gated */}
        {enhanced_data && enhanced_data.top_locations.length > 0 && (
          <div className={styles.locationsSection}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>📍</span>
              <h3>Best Locations for {formatCareerTitle(career.title)}</h3>
              {!isPremium && <span className={styles.premiumBadge}>🔒 Blueprint</span>}
            </div>
            {isPremium ? (
              <>
                <div className={styles.locationsGrid}>
                  {enhanced_data.top_locations.map((loc, i) => (
                    <div key={i} className={styles.locationCard}>
                      <div className={styles.locationHeader}>
                        <span className={styles.locationRank}>#{i + 1}</span>
                        <span className={styles.locationName}>{loc.city}, {loc.state}</span>
                      </div>
                      <div className={styles.locationStats}>
                        <div className={styles.locationStat}>
                          <span className={styles.statLabel}>Salary</span>
                          <span className={styles.statValue}>
                            ${(loc.salary.min / 1000).toFixed(0)}K - ${(loc.salary.max / 1000).toFixed(0)}K
                          </span>
                        </div>
                        <div className={styles.locationStat}>
                          <span className={styles.statLabel}>COL-Adjusted</span>
                          <span className={styles.statValue}>
                            ${(loc.col_adjusted.min / 1000).toFixed(0)}K - ${(loc.col_adjusted.max / 1000).toFixed(0)}K
                          </span>
                        </div>
                        <div className={styles.locationStat}>
                          <span className={styles.statLabel}>Job Openings</span>
                          <span className={styles.statValue}>{loc.job_openings.toLocaleString()}+</span>
                        </div>
                        <div className={styles.locationStat}>
                          <span className={styles.statLabel}>Competition</span>
                          <span className={`${styles.competitionLevel} ${styles[`competition${loc.competition.charAt(0).toUpperCase() + loc.competition.slice(1)}`]}`}>
                            {loc.competition.charAt(0).toUpperCase() + loc.competition.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Place Finder Cross-sell */}
                {enhanced_data.geo_finder_rec && (
                  <div className={styles.geoFinderCrossSell}>
                    <div className={styles.crossSellContent}>
                      <h4>🌎 Find Your Perfect Location</h4>
                      <p>
                        Want detailed relocation guidance? Our <strong>Place Finder</strong> tool
                        analyzes cost of living, job markets, and lifestyle factors to find
                        the best city for your career and budget.
                      </p>
                      {enhanced_data.geo_finder_rec.best_for_remote && (
                        <p className={styles.remoteNote}>
                          ✨ This career also has excellent <strong>remote work opportunities</strong>!
                        </p>
                      )}
                    </div>
                    <a
                      href={enhanced_data.geo_finder_rec.geo_finder_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.crossSellButton}
                    >
                      Explore Place Finder →
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.lockedContent}>
                <div className={styles.lockedPreview}>
                  <div className={styles.locationsGrid} style={{ opacity: 0.3, pointerEvents: 'none' }}>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={styles.locationCard}>
                        <div className={styles.locationHeader}>
                          <span className={styles.locationRank}>#{i}</span>
                          <span className={styles.locationName}>████████, ██</span>
                        </div>
                        <div className={styles.locationStats}>
                          <div className={styles.locationStat}>
                            <span className={styles.statLabel}>Salary</span>
                            <span className={styles.statValue}>$██K - $██K</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={styles.lockedOverlay}>
                  <span className={styles.lockIcon}>🔒</span>
                  <p><strong>Top {enhanced_data.top_locations.length} locations</strong> with salary & job market data</p>
                  <p className={styles.lockedHint}>Compare COL-adjusted salaries and find where to maximize your income</p>
                  <button
                    className={styles.unlockButton}
                    onClick={handlePremiumCheckout}
                    disabled={isCheckoutLoading}
                  >
                    {isCheckoutLoading ? 'Loading...' : 'Unlock Blueprint - $29'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {enhancedMatchReasons.length > 0 && (
          <div className={styles.matchReasons}>
            <h3>Why This Match?</h3>
            <ul>
              {enhancedMatchReasons.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
        
        <div className={styles.card}>
          <p className={styles.description}>{career.description}</p>

          {/* Actionable Job Titles Section */}
          {enhancedCareer.actionable_titles.length > 1 && (
            <div className={styles.section}>
              <h3>🔍 Search For These Job Titles</h3>
              <p className={styles.sectionSubtitle}>
                Use these specific titles when searching on Indeed, LinkedIn, or other job boards:
              </p>
              <div className={styles.skillTags}>
                {enhancedCareer.actionable_titles.slice(0, 6).map((title, i) => (
                  <span key={i} className={styles.actionableTitle}>{title}</span>
                ))}
              </div>
            </div>
          )}

          {/* Specializations Section */}
          {enhancedCareer.specializations.length > 0 && (
            <div className={styles.section}>
              <h3>📋 Career Specializations</h3>
              <div className={styles.specializationsList}>
                {enhancedCareer.specializations.slice(0, 3).map((spec, i) => (
                  <div key={i} className={styles.specializationItem}>
                    <strong>{spec.title}</strong>
                    <p>{spec.description}</p>
                    <span className={styles.employerHint}>
                      Common employers: {spec.typical_employers.slice(0, 3).join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Premium Upsell - Show paywall or dev mode content */}
          {devMode ? (
            <div className={styles.premiumTeaser} style={{ borderColor: '#10b981', background: 'rgba(16, 185, 129, 0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ background: '#10b981', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>DEV MODE</span>
                <h4 style={{ margin: 0 }}>🔓 Premium Content Unlocked</h4>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
                Bypass active via <code>?devtest=true</code> or <code>?unlocked=true</code>.
                In production, users would see their personalized Career Blueprint here after payment.
              </p>
              <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px', marginTop: '1rem' }}>
                <strong>Premium features to implement:</strong>
                <ul style={{ margin: '0.5rem 0 0 1rem', fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
                  <li>90-day personalized action plan</li>
                  <li>Specific employers hiring for {formatCareerTitle(career.title)}</li>
                  <li>Required certifications with direct links</li>
                  <li>Salary negotiation tips for this role</li>
                  <li>Resume keywords and templates</li>
                  <li>Interview prep questions</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className={styles.premiumTeaser}>
              <h4>🚀 Want a Personalized Career Blueprint?</h4>
              {enhancedCareer.has_premium_data && enhancedCareer.premium_preview.length > 0 ? (
                <p>
                  Unlock detailed paths to roles like <strong>{enhancedCareer.premium_preview[0]?.title}</strong>
                  {enhancedCareer.premium_preview[0]?.salary_range && (
                    <> (${(enhancedCareer.premium_preview[0].salary_range.min / 1000).toFixed(0)}K - ${(enhancedCareer.premium_preview[0].salary_range.max / 1000).toFixed(0)}K)</>
                  )} with specific employers, certifications, and job board links.
                </p>
              ) : (
                <p>
                  Get a <strong>personalized 90-day action plan</strong> for becoming a {formatCareerTitle(career.title)},
                  including specific employers hiring now, required certifications, and step-by-step guidance.
                </p>
              )}
              <button
                className={styles.premiumButton}
                onClick={handlePremiumCheckout}
                disabled={isCheckoutLoading}
              >
                {isCheckoutLoading ? 'Loading...' : `${segmentConfig.premiumCtaText} ($29) →`}
              </button>
            </div>
          )}

          {/* Resume Builder Section - Complete Tier Feature */}
          {hasCompleteTier ? (
            <div className={styles.resumeSection}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionIcon}>📄</span>
                <h3>Generate Your Resume</h3>
              </div>
              <p className={styles.resumeDescription}>
                Create a professional, ATS-optimized resume tailored for {formatCareerTitle(career.title)} positions.
              </p>
              <ResumeGenerator
                diagnosticId={'preview'}
                topCareer={career}
              />
            </div>
          ) : isPremium ? (
            <div className={styles.resumeUpgrade}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionIcon}>📄</span>
                <h3>Resume Builder</h3>
                <span className={styles.premiumBadge}>🔒 Complete</span>
              </div>
              <p style={{ color: 'var(--pf-text-secondary)', marginBottom: '1rem' }}>
                Upgrade to <strong>Complete</strong> to generate a professional resume for {formatCareerTitle(career.title)}.
              </p>
              <button
                className={styles.upgradeButton}
                onClick={async () => {
                  setIsCheckoutLoading(true);
                  try {
                    const response = await fetch('/api/checkout', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ tier: 'complete' }),
                    });
                    const data = await response.json();
                    if (data.url) window.location.href = data.url;
                  } catch (error) {
                    console.error('Checkout error:', error);
                  } finally {
                    setIsCheckoutLoading(false);
                  }
                }}
                disabled={isCheckoutLoading}
              >
                {isCheckoutLoading ? 'Loading...' : 'Upgrade to Complete - $49'}
              </button>
            </div>
          ) : null}

          {/* Premium Career Tools (Blueprint+ Tier) */}
          {isPremium && (
            <PremiumToolsNavigator
              career={career}
              userSegment={userSegment}
              hasCompleteTier={hasCompleteTier}
              matchReasons={result.match_reasons || []}
            />
          )}

          <div className={styles.salarySection}>
            <span className={styles.salaryLabel}>Entry-Level Salary</span>
            <p className={styles.salaryRange}>
              ${(career.salary.entry_min / 1000).toFixed(0)}K - ${(career.salary.entry_max / 1000).toFixed(0)}K/year
            </p>
          </div>

          <div className={styles.section}>
            <h3>What You&apos;ll Do Daily</h3>
            <ul>
              {career.dailyActivities.map((activity, i) => (
                <li key={i}>{activity}</li>
              ))}
            </ul>
          </div>

          <div className={styles.section}>
            <h3>Skills You Need</h3>
            <div className={styles.skillTags}>
              {career.requiredSkills.map((skill, i) => (
                <span key={i} className={styles.skillTag}>{skill}</span>
              ))}
            </div>
          </div>

          <div className={styles.scoreBreakdown}>
            <h3>Score Breakdown</h3>
            <div className={styles.scoreBar}>
              <span>Skill Match</span>
              <div className={styles.bar}>
                <div className={styles.barFill} style={{ width: `${scores.skill_match}%` }} />
              </div>
              <span>{Math.round(scores.skill_match)}%</span>
            </div>
            <div className={styles.scoreBar}>
              <span>Lifestyle Fit</span>
              <div className={styles.bar}>
                <div className={styles.barFill} style={{ width: `${scores.lifestyle_fit}%` }} />
              </div>
              <span>{Math.round(scores.lifestyle_fit)}%</span>
            </div>
            <div className={styles.scoreBar}>
              <span>Market Viability</span>
              <div className={styles.bar}>
                <div className={styles.barFill} style={{ width: `${scores.market_viability}%` }} />
              </div>
              <span>{Math.round(scores.market_viability)}%</span>
            </div>
            <div className={styles.scoreBar}>
              <span>Salary Fit</span>
              <div className={styles.bar}>
                <div className={styles.barFill} style={{ width: `${scores.salary_fit}%` }} />
              </div>
              <span>{Math.round(scores.salary_fit)}%</span>
            </div>
          </div>
        </div>

        {/* Pathway Recommendations */}
        {pathways && pathways.length > 0 && (
          <div className={styles.pathwaysSection}>
            <h2 className={styles.pathwaysTitle}>📍 Your Career Pathways</h2>
            <p className={styles.pathwaysSubtitle}>
              We found <strong>{pathways.length} concrete pathways</strong> to help you reach {career.title} and similar careers.
              {pathwayMetadata && (
                <span className={styles.pathwayMeta}>
                  {' '}({pathwayMetadata.ai_enabled ? `AI-enhanced with ${pathwayMetadata.ai_provider}` : 'Template-based'}
                  {pathwayMetadata.generation_cost > 0 && ` - $${pathwayMetadata.generation_cost.toFixed(4)}`})
                </span>
              )}
            </p>

            {pathways.map((pathwayRec, index) => {
              const pathway = pathwayRec.pathway;
              const totalMonths = pathway.total_time_years.typical * 12;
              const totalYears = pathway.total_time_years.typical;

              return (
                <div key={index} className={styles.pathwayCard}>
                  <div className={styles.pathwayHeader}>
                    <h3>{pathwayRec.template_name || `Pathway ${index + 1}`}</h3>
                    <div className={styles.pathwayBadges}>
                      {pathwayRec.source === 'template' && (
                        <span className={styles.badge}>✓ Proven Path</span>
                      )}
                      {pathwayRec.source === 'template_ai_enhanced' && (
                        <span className={styles.badgeAI}>🤖 AI Enhanced</span>
                      )}
                      <span className={styles.badgeScore}>
                        {Math.round(pathwayRec.match_score)}% Match
                      </span>
                    </div>
                  </div>

                  <div className={styles.pathwayStats}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Timeline:</span>
                      <span className={styles.statValue}>
                        {totalYears < 1 ? `${totalMonths} months` : `${totalYears.toFixed(1)} years`}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Investment:</span>
                      <span className={styles.statValue}>
                        ${pathway.total_cost.typical.toLocaleString()}
                        {pathway.total_cost.gi_bill_covers_percentage > 0 && (
                          <span className={styles.giBillNote}> ({pathway.total_cost.gi_bill_covers_percentage}% G.I. Bill)</span>
                        )}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>ROI:</span>
                      <span className={styles.statValue}>
                        {pathway.roi_analysis.break_even_years < 1
                          ? `${(pathway.roi_analysis.break_even_years * 12).toFixed(0)} months`
                          : `${pathway.roi_analysis.break_even_years.toFixed(1)} years`} to break even
                      </span>
                    </div>
                  </div>

                  <div className={styles.pathwayMilestones}>
                    <h4>Key Steps:</h4>
                    <ol>
                      {pathway.milestones.slice(0, 4).map((milestone: { role_title: string; duration_months: number; cost: number }, mIndex: number) => (
                        <li key={mIndex}>
                          <strong>{milestone.role_title}</strong>
                          {' '}({milestone.duration_months} mo
                          {milestone.cost > 0 && `, $${milestone.cost.toLocaleString()}`})
                        </li>
                      ))}
                      {pathway.milestones.length > 4 && (
                        <li className={styles.moreSteps}>
                          + {pathway.milestones.length - 4} more steps
                        </li>
                      )}
                    </ol>
                  </div>

                  {pathway.milestones[0]?.immediate_actions && pathway.milestones[0].immediate_actions.length > 0 && (
                    <div className={styles.nextActions}>
                      <h4>🚀 Start Today:</h4>
                      <ul>
                        {pathway.milestones[0].immediate_actions.slice(0, 2).map((action: { description: string; time_required: string }, aIndex: number) => (
                          <li key={aIndex}>
                            {action.description} <em>({action.time_required})</em>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}

            <div className={styles.pathwaysCTA}>
              <p>💡 <strong>These are proven pathways</strong> that have worked for others in similar situations. Choose the one that fits your timeline and budget, and start with the first action today.</p>
            </div>
          </div>
        )}

        {/* Success Story Submission CTA */}
        <div className={styles.successStoryCta}>
          <div className={styles.successStoryHeader}>
            <span className={styles.successStoryIcon}>🌟</span>
            <div>
              <h3>Already Made This Transition?</h3>
              <p>Inspire others by sharing your career change story</p>
            </div>
          </div>
          <button
            className={styles.shareStoryButton}
            onClick={() => setShowSuccessStory(true)}
          >
            Share Your Story
          </button>
        </div>

        {/* Success Story Submission Modal */}
        {showSuccessStory && (
          <SuccessStorySubmission
            career={career}
            userSegment={userSegment}
            onClose={() => setShowSuccessStory(false)}
          />
        )}

        <button
          onClick={() => {
            setStep(0);
            setAnswers({});
            setResult(null);
            setPathways([]);
            setPathwayMetadata(null);
          }}
          className={styles.restartButton}>
          Start Over
        </button>
      </div>
    );
  }

  // Get current substep for two_step questions
  const currentSubstep = currentQuestion.type === 'two_step' && currentQuestion.substeps
    ? currentQuestion.substeps[twoStepSubstep]
    : null;

  // Get dynamic content for two_step text substeps
  const dynamicContent = currentSubstep?.type === 'text' && currentSubstep.promptMap && currentQuestion.substeps
    ? (() => {
        const firstSubstep = currentQuestion.substeps[0];
        const firstAnswer = answers[firstSubstep.id];
        return firstAnswer && currentSubstep.promptMap[firstAnswer]
          ? currentSubstep.promptMap[firstAnswer]
          : null;
      })()
    : null;

  // Calculate progress percentage
  const progressPercent = ((step + 1) / activeQuestions.length) * 100;
  const estimatedMinutesLeft = Math.ceil((activeQuestions.length - step - 1) * 0.4);

  return (
    <div className={styles.container}>
      {/* Visual Progress Bar */}
      <div className={styles.progressWrapper}>
        <div className={styles.progressBarTrack}>
          <div
            className={styles.progressBarFill}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className={styles.progressInfo}>
          <span className={styles.progressText}>
            Question {step + 1} of {activeQuestions.length}
            {currentQuestion.type === 'two_step' && currentQuestion.substeps && (
              <span className={styles.substepIndicator}> • Part {twoStepSubstep + 1}/{currentQuestion.substeps.length}</span>
            )}
          </span>
          <span className={styles.progressTime}>
            {estimatedMinutesLeft > 0 ? `~${estimatedMinutesLeft} min left` : 'Almost done!'}
          </span>
        </div>
      </div>

      <div className={styles.questionCard}>
        {/* 5-Factor Matching Context - Show on first question */}
        {step === 0 && (
          <div className={styles.matchingContext}>
            <div className={styles.matchingContextHeader}>
              <span className={styles.matchingContextIcon}>🎯</span>
              <span className={styles.matchingContextTitle}>How PathFinder Matches You</span>
            </div>
            <p className={styles.matchingContextDescription}>
              We analyze <strong>5 key factors</strong> to find careers that truly fit:
            </p>
            <ul className={styles.matchingFactors}>
              <li><strong>Skills</strong> — What you already know and do well</li>
              <li><strong>Lifestyle</strong> — Work environment, hours, and flexibility you prefer</li>
              <li><strong>Salary</strong> — Income goals and financial needs</li>
              <li><strong>Training</strong> — Time and resources you can invest</li>
              <li><strong>Market Demand</strong> — Jobs that are actually hiring</li>
            </ul>
          </div>
        )}

        {/* Segment-specific header note (personalization) */}
        {(() => {
          const questionOverride = getQuestionOverride(userSegment, currentQuestion.id);
          return questionOverride?.headerNote ? (
            <p className={styles.segmentHeaderNote}>{questionOverride.headerNote}</p>
          ) : null;
        })()}

        <h2 className={styles.questionText}>
          {currentSubstep ? currentSubstep.text : currentQuestion.text}
        </h2>

        {/* Segment-specific encouragement or default encouragement text */}
        {(() => {
          const questionOverride = getQuestionOverride(userSegment, currentQuestion.id);
          // Priority: segment override > question default (for two_step text) > question encouragementText
          if (questionOverride?.encouragementText) {
            return <p className={styles.encouragement}>{questionOverride.encouragementText}</p>;
          }
          // Fall back to default question encouragement for two_step text substeps
          if (currentQuestion.type === 'two_step' && currentQuestion.encouragementText && currentSubstep?.type === 'text') {
            return <p className={styles.encouragement}>{currentQuestion.encouragementText}</p>;
          }
          // Show encouragement for regular questions with encouragementText
          if (currentQuestion.encouragementText && currentQuestion.type !== 'two_step') {
            return <p className={styles.encouragement}>{currentQuestion.encouragementText}</p>;
          }
          return null;
        })()}

        {/* Two-step question rendering */}
        {currentQuestion.type === 'two_step' && currentSubstep && (
          <>
            {/* First substep - single choice */}
            {currentSubstep.type === 'single' && currentSubstep.options && (
              <div className={styles.optionsContainer}>
                {currentSubstep.options.map((option: QuestionOption) => (
                  <button
                    key={option.id}
                    onClick={() => handleAnswer(option.id)}
                    className={`${styles.option} ${
                      currentAnswer === option.id ? styles.selected : ''
                    }`}
                  >
                    {option.text}
                  </button>
                ))}
              </div>
            )}

            {/* Second substep - text with dynamic guidance */}
            {currentSubstep.type === 'text' && (
              <div>
                {dynamicContent && (
                  <div className={styles.guidanceBox}>
                    <div className={styles.startersSection}>
                      <h4>Pick a sentence starter to help you begin:</h4>
                      <div className={styles.starters}>
                        {dynamicContent.starters.map((starter, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              const textarea = document.querySelector('textarea');
                              if (textarea) {
                                textarea.value = starter;
                                handleAnswer(starter);
                                textarea.focus();
                              }
                            }}
                            className={styles.starterButton}
                          >
                            {starter}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className={styles.exampleSection}>
                      <h4>Example:</h4>
                      <p className={styles.exampleText}>{dynamicContent.example}</p>
                    </div>

                    <p className={styles.encouragementInline}>{dynamicContent.encouragement}</p>
                  </div>
                )}

                <textarea
                  value={currentAnswer || ''}
                  onChange={(e) => handleAnswer(e.target.value)}
                  placeholder={currentSubstep.placeholder}
                  className={styles.textarea}
                  rows={6}
                />

                <VoiceInput
                  onTranscript={(text) => {
                    const current = currentAnswer || '';
                    const newValue = current ? `${current} ${text}`.trim() : text;
                    handleAnswer(newValue);
                  }}
                />

                {currentQuestion.allowSkip && currentQuestion.skipText && (
                  <button
                    onClick={() => handleAnswer('SKIPPED')}
                    className={styles.skipButton}
                  >
                    {currentQuestion.skipText}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {currentQuestion.type === 'single' && currentQuestion.options && (
          <div className={styles.optionsContainer}>
            {currentQuestion.options.map((option: QuestionOption) => (
              <button
                key={option.id}
                onClick={() => handleAnswer(option.id)}
                className={`${styles.option} ${
                  currentAnswer === option.id ? styles.selected : ''
                }`}
              >
                {option.text}
              </button>
            ))}
          </div>
        )}

        {currentQuestion.type === 'multiple' && currentQuestion.options && (
          <div className={styles.optionsContainer}>
            {currentQuestion.options.map((option: QuestionOption) => {
              const isSelected = Array.isArray(currentAnswer) && currentAnswer.includes(option.id);
              const isNoneOption = option.id === 'none';
              const hasNoneSelected = Array.isArray(currentAnswer) && currentAnswer.includes('none');
              
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    const current = Array.isArray(currentAnswer) ? currentAnswer : [];
                    
                    if (isSelected) {
                      // Deselect this option
                      handleAnswer(current.filter(id => id !== option.id));
                    } else {
                      // Selecting a new option
                      if (isNoneOption) {
                        // If selecting "none", clear all other selections
                        handleAnswer(['none']);
                      } else {
                        // If selecting a regular option, remove "none" if present and add this option
                        const withoutNone = current.filter(id => id !== 'none');
                        handleAnswer([...withoutNone, option.id]);
                      }
                    }
                  }}
                  className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                  disabled={!isNoneOption && hasNoneSelected && !isSelected}
                >
                  {option.text}
                </button>
              );
            })}
          </div>
        )}

        {currentQuestion.type === 'text' && (
          <textarea
            value={currentAnswer || ''}
            onChange={(e) => handleAnswer(e.target.value)}
            placeholder={currentQuestion.placeholder}
            className={styles.textarea}
            rows={6}
          />
        )}
      </div>

      <div className={styles.navigation}>
        {step > 0 && (
          <button onClick={handleBack} className={styles.backButton}>
            Back
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={!canProceed}
          className={styles.nextButton}
        >
          {step === activeQuestions.length - 1 ? 'See My Results' : 'Next'}
        </button>
      </div>
    </div>
  );
}
